import { User } from 'module/api/user/types';
import { useTranslation } from 'module/hooks/use-translation';
import { EmptyState } from 'module/ui/components/atoms/empty-state';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { useDeleteConfirmModal } from 'module/ui/components/atoms/modal/use-delete-confirm-modal';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPage } from '@fortawesome/pro-regular-svg-icons';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';
import { BlacklistedDomain, useDeleteBlacklistedDomain } from 'queries/emails';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { fullName } from 'utils/full-name';
import {
  FieldListWrapper,
  HeaderRow,
  HeaderRowItem,
  FieldRow as Row,
  FieldRowItem,
  MoreActionsRowItem,
} from 'components/organisms/signal-fields/components';

interface DomainRowProps {
  domain: {
    id: string;
    email: string;
    updatedBy: User;
    updatedOn: string;
  };
  onEdit: (domain: BlacklistedDomain) => void;
}

const StyledMoreActionsRowItem = styled(MoreActionsRowItem)`
  display: flex;
  justify-content: flex-end;
`;
const DomainRow: React.FC<DomainRowProps> = ({ domain, onEdit }) => {
  const formatDate = (dateString: string) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  const { mutate: deleteBlacklistedDomain } = useDeleteBlacklistedDomain();

  const { open: handleDelete, dialog: deleteConfirmationDialog } =
    useDeleteConfirmModal({
      width: 476,
      title: useTranslation('settings.domain_blacklist.delete.confirm.title'),
      content: useTranslation(
        'settings.domain_blacklist.delete.confirm.message',
      ),
      okText: useTranslation('remove'),
      onOk: () => {
        deleteBlacklistedDomain({ email: domain.email });
      },
    });

  return (
    <>
      <Row>
        <FieldRowItem>{domain.email}</FieldRowItem>
        <FieldRowItem>{fullName(domain.updatedBy)}</FieldRowItem>
        <FieldRowItem>{formatDate(domain.updatedOn)}</FieldRowItem>
        <StyledMoreActionsRowItem>
          <MoreActionsDropdown
            menu={{
              onClick: ({ key }) => {
                switch (key) {
                  case 'delete':
                    handleDelete();
                    break;
                  case 'edit':
                    onEdit(domain);
                    break;
                }
              },
              items: [
                {
                  type: 'group',
                  label: <FormattedMessage id="more_actions" />,
                  children: [
                    {
                      key: 'edit',
                      label: <FormattedMessage id="edit" />,
                      icon: <FontAwesomeIcon icon={faEdit} />,
                    },
                    {
                      key: 'delete',
                      label: <FormattedMessage id="delete" />,
                      icon: <FontAwesomeIcon icon={faTrash} />,
                      danger: true,
                    },
                  ],
                },
              ],
            }}
          />
        </StyledMoreActionsRowItem>
      </Row>
      {deleteConfirmationDialog}
    </>
  );
};

const StyledHeaderRowItem = styled(HeaderRowItem)`
  justify-content: flex-end;
  display: flex;
  align-items: center;
`;

const StickyHeaderRow = styled(HeaderRow)`
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--white-100);
`;

export const DomainsList: React.FC<{
  domains: BlacklistedDomain[];
  onEdit: (domain: BlacklistedDomain) => void;
  onCreate: () => void;
}> = ({ domains, onEdit, onCreate }) => {
  return (
    <FieldListWrapper>
      {domains && domains.length > 0 && (
        <StickyHeaderRow>
          <HeaderRowItem>
            <FormattedMessage id="email" defaultMessage="Email" />
          </HeaderRowItem>
          <HeaderRowItem>
            <FormattedMessage id="updated_by" defaultMessage="Updated By" />
          </HeaderRowItem>
          <HeaderRowItem>
            <FormattedMessage id="updated_on" defaultMessage="Updated On" />
          </HeaderRowItem>
          <StyledHeaderRowItem>
            <FormattedMessage id="actions" defaultMessage="Actions" />
          </StyledHeaderRowItem>
        </StickyHeaderRow>
      )}
      {domains && domains.length > 0 ? (
        domains.map((domain) => (
          <DomainRow key={domain.id} domain={domain} onEdit={onEdit} />
        ))
      ) : domains.length === 0 ? (
        <EmptyState
          image={<FontAwesomeIcon icon={faPage} size={40} />}
          title={<FormattedMessage id="settings.domain_blacklist.no_domains" />}
          message={
            <FormattedMessage id="settings.domain_blacklist.no_domains_description" />
          }
          primaryCTA={
            <EmptyState.CallToAction.Primary
              leftIcon={<FontAwesomeIcon icon={faPlus} size={16} />}
              onClick={() => {
                onCreate?.();
              }}
            >
              <FormattedMessage id="settings.domain_blacklist.add_domain" />
            </EmptyState.CallToAction.Primary>
          }
        />
      ) : null}
    </FieldListWrapper>
  );
};
